import React, { useState } from 'react';
import { toast } from 'react-hot-toast';
import { NativeDateInput, NativeTimeInput } from '../ui/NativeDateInput';
import { supabase } from '../../lib/supabase';
import { useTeamStore } from '../../store/teamStore';
import { getGamePlanTitle } from '../../utils/gamePlanUtils';
import type { GamePlan } from '../../types';

interface GamePlanEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  gamePlan: GamePlan;
}



const GamePlanEditModal: React.FC<GamePlanEditModalProps> = ({
  isOpen, 
  onClose, 
  onSuccess,
  gamePlan
}) => {
  const { currentTeam } = useTeamStore();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: gamePlan.title || '',
    opponent_name: gamePlan.opponent_name || '',
    location: gamePlan.location || '',
    game_date: gamePlan.game_date || '',
    game_time: gamePlan.game_time || '',
    home_away: gamePlan.home_away || 'home',
    key_matchups: gamePlan.key_matchups || '',
    special_considerations: gamePlan.special_considerations || '',
    offensive_strategy: gamePlan.offensive_strategy || {},
    defensive_strategy: gamePlan.defensive_strategy || {}
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('game_plans')
        .update({
          title: formData.title,
          opponent_name: formData.opponent_name,
          location: formData.location,
          game_date: formData.game_date,
          game_time: formData.game_time,
          home_away: formData.home_away,
          key_matchups: formData.key_matchups,
          special_considerations: formData.special_considerations,
          offensive_strategy: formData.offensive_strategy,
          defensive_strategy: formData.defensive_strategy,
          updated_at: new Date().toISOString()
        })
        .eq('id', gamePlan.id);

      if (error) throw error;
      
      toast.success('Game plan updated successfully!');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error updating game plan:', error);
      toast.error('Failed to update game plan');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };



  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold">
            Edit Game Plan: {getGamePlanTitle({ ...gamePlan, ...formData }, currentTeam)}
          </h2>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(80vh - 140px)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Game Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game Plan Title
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="vs Eagles - Week 3"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Opponent Name *
                </label>
                <input
                  type="text"
                  value={formData.opponent_name}
                  onChange={(e) => handleInputChange('opponent_name', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Eagles"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game Date *
                </label>
                <NativeDateInput
                  value={formData.game_date}
                  onChange={(e) => handleInputChange('game_date', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Game Time
                </label>
                <NativeTimeInput
                  value={formData.game_time}
                  onChange={(e) => handleInputChange('game_time', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Stadium name or specific venue"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Home / Away *
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="home_away"
                      value="home"
                      checked={formData.home_away === 'home'}
                      onChange={(e) => handleInputChange('home_away', e.target.value)}
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Home</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      name="home_away"
                      value="away"
                      checked={formData.home_away === 'away'}
                      onChange={(e) => handleInputChange('home_away', e.target.value)}
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="ml-2 text-sm text-gray-700">Away</span>
                  </label>
                </div>
              </div>
            </div>


          </form>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="btn-secondary"
              disabled={isLoading}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Updating...' : 'Update Game Plan'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GamePlanEditModal; 